import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { HeartPulse, Plus, Activity, History, Trash2, ChevronLeft, ChevronRight, CalendarClock } from "lucide-react";
import api, { fmtDate } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { useFirma, OperatoreLocked } from "../context/FirmaContext";

export const PARAMS = [
  { key: "pressione_sis", label: "Pressione max", short: "P.max", unit: "mmHg" },
  { key: "pressione_dia", label: "Pressione min", short: "P.min", unit: "mmHg" },
  { key: "fc", label: "Freq. cardiaca", short: "FC", unit: "bpm" },
  { key: "temp", label: "Temperatura", short: "Temp", unit: "°C" },
  { key: "spo2", label: "Saturazione O₂", short: "SpO₂", unit: "%" },
  { key: "glicemia", label: "Glicemia", short: "Glic", unit: "mg/dl" },
  { key: "fr", label: "Freq. respiratoria", short: "FR", unit: "atti/min" },
  { key: "peso", label: "Peso", short: "Peso", unit: "kg" },
  { key: "dolore", label: "Dolore", short: "Dolore", unit: "0-10" },
];

export const flag = (key, v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  switch (key) {
    case "temp": return n >= 38 ? "danger" : n >= 37.5 ? "warn" : "";
    case "spo2": return n < 92 ? "danger" : n < 95 ? "warn" : "";
    case "pressione_sis": return n >= 160 || n < 90 ? "danger" : n >= 140 ? "warn" : "";
    case "pressione_dia": return n >= 100 || n < 50 ? "danger" : n >= 90 ? "warn" : "";
    case "fc": return n > 110 || n < 45 ? "danger" : n > 100 || n < 50 ? "warn" : "";
    case "glicemia": return n >= 250 || n < 60 ? "danger" : n >= 180 || n < 70 ? "warn" : "";
    case "fr": return n > 24 || n < 8 ? "danger" : n > 20 || n < 10 ? "warn" : "";
    case "dolore": return n >= 7 ? "danger" : n >= 4 ? "warn" : "";
    default: return "";
  }
};
export const flagClass = (f) => f === "danger" ? "bg-red-100 text-red-700 font-bold" : f === "warn" ? "bg-amber-100 text-amber-700 font-semibold" : "";
export const fmtDateTime = (iso) => { if (!iso) return "—"; const d = new Date(iso); return `${fmtDate(iso)} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };

const CHART_COLORS = { temp: "#e11d48", spo2: "#2563eb", pressione_sis: "#7c3aed", pressione_dia: "#a78bfa", fc: "#0891b2", glicemia: "#ea580c", fr: "#16a34a", peso: "#64748b", dolore: "#db2777" };
const CHART_OPTIONS = [
  { v: "temp_spo2", l: "Temperatura + SpO₂" },
  { v: "pressione", l: "Pressione (max/min)" },
  { v: "fc", l: "Frequenza cardiaca" },
  { v: "glicemia", l: "Glicemia" },
  { v: "fr", l: "Freq. respiratoria" },
  { v: "peso", l: "Peso" },
  { v: "dolore", l: "Dolore" },
];
const MESI_P = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const curMeseP = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`; };
const meseLblP = (mese) => { const [y, m] = mese.split("-").map(Number); return `${MESI_P[m - 1]} ${y}`; };
const PLABEL = Object.fromEntries(PARAMS.map((p) => [p.key, `${p.short} ${p.unit}`.trim()]));

export function ParametriChart({ data }) {
  const [sel, setSel] = useState("temp_spo2");
  const need = sel === "temp_spo2" ? ["temp", "spo2"] : sel === "pressione" ? ["pressione_sis", "pressione_dia"] : [sel];
  const pts = [...(data || [])].reverse().slice(-30)
    .filter((r) => need.some((k) => r[k] != null))
    .map((r) => { const o = { t: fmtDate(r.data).slice(0, 5) }; need.forEach((k) => { o[k] = r[k]; }); return o; });
  return (
    <div data-testid="par-chart">
      <div className="flex justify-end mb-2">
        <div className="w-56">
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger data-testid="par-chart-select" className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{CHART_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {pts.length < 2 ? (
        <div className="text-xs text-muted-foreground text-center py-8">Servono almeno 2 misurazioni per vedere l'andamento di questo parametro.</div>
      ) : (
        <div className="h-56 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={pts} margin={{ top: 5, right: 10, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="t" tick={{ fontSize: 11 }} />
              {sel === "temp_spo2" ? (
                <>
                  <YAxis yAxisId="l" domain={["auto", "auto"]} tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="r" orientation="right" domain={[80, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line yAxisId="l" type="monotone" dataKey="temp" name="Temp °C" stroke={CHART_COLORS.temp} strokeWidth={2} dot={{ r: 2 }} connectNulls />
                  <Line yAxisId="r" type="monotone" dataKey="spo2" name="SpO₂ %" stroke={CHART_COLORS.spo2} strokeWidth={2} dot={{ r: 2 }} connectNulls />
                </>
              ) : (
                <>
                  <YAxis domain={["auto", "auto"]} tick={{ fontSize: 11 }} />
                  <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
                  {need.map((k) => <Line key={k} type="monotone" dataKey={k} name={PLABEL[k]} stroke={CHART_COLORS[k]} strokeWidth={2} dot={{ r: 2 }} connectNulls />)}
                </>
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export function ParametriOspite({ ospite }) {
  const qc = useQueryClient();
  const { opName, signerName } = useFirma();
  const [operatori, setOperatori] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({});
  const { data: recs = [] } = useQuery({ queryKey: ["parametri-storico", ospite.id], queryFn: async () => (await api.get(`/parametri/storico/${ospite.id}`)).data });
  useEffect(() => { api.get("/firma/operatori", { params: { structure_id: ospite.structure_id } }).then((r) => setOperatori(r.data)).catch(() => setOperatori([])); }, [ospite.structure_id]);
  const latest = recs[0];
  const [mese, setMese] = useState(curMeseP());
  const [freq, setFreq] = useState(ospite.parametri_frequenza_giorni ?? "");
  const [savedFreq, setSavedFreq] = useState(Number(ospite.parametri_frequenza_giorni || 0));
  const [freqBusy, setFreqBusy] = useState(false);
  useEffect(() => { setFreq(ospite.parametri_frequenza_giorni ?? ""); setSavedFreq(Number(ospite.parametri_frequenza_giorni || 0)); }, [ospite.id, ospite.parametri_frequenza_giorni]);
  const saveFreq = async () => {
    setFreqBusy(true);
    try {
      const n = freq === "" ? 0 : Math.max(0, Math.round(Number(freq)));
      await api.put(`/ospiti/${ospite.id}`, { parametri_frequenza_giorni: n });
      setSavedFreq(n);
      qc.invalidateQueries({ queryKey: ["parametri-da-misurare"] });
      qc.invalidateQueries({ queryKey: ["mod", "ospiti"] });
      toast.success(n > 0 ? `Programmato ogni ${n} giorni` : "Programmazione rimossa");
    } catch { toast.error("Errore nel salvataggio"); } finally { setFreqBusy(false); }
  };
  const shiftMeseP = (n) => { const [y, m] = mese.split("-").map(Number); const d = new Date(y, m - 1 + n, 1); setMese(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); };
  const meseRecs = recs.filter((r) => (r.data || "").startsWith(mese));

  const save = async () => {
    if (!form.operatore) { toast.error("Seleziona l'operatore"); return; }
    if (!PARAMS.some((p) => form[p.key] != null && form[p.key] !== "")) { toast.error("Inserisci almeno un parametro"); return; }
    try {
      const body = { ospite_id: ospite.id, structure_id: ospite.structure_id, operatore: form.operatore, note: form.note || "" };
      PARAMS.forEach((p) => { body[p.key] = form[p.key]; });
      await api.post("/parametri/register", body);
      qc.invalidateQueries({ queryKey: ["parametri-storico", ospite.id] });
      setDlg(false); setForm({}); toast.success("Parametri registrati");
    } catch { toast.error("Errore nel salvataggio"); }
  };
  const delRec = async (rec) => { if (!signerName) { toast.error("Sblocca con la Firma Rapida (PIN)"); return; } if (!window.confirm("Eliminare questa misurazione? Resterà nell'Elenco correzioni.")) return; try { await api.delete(`/parametri/rec/${rec.id}`, { params: { operatore: signerName } }); qc.invalidateQueries({ queryKey: ["parametri-storico", ospite.id] }); qc.invalidateQueries({ queryKey: ["correzioni"] }); } catch { toast.error("Errore"); } };

  return (
    <div className="space-y-4" data-testid="scheda-parametri">
      <div className="gr-card p-4" data-testid="scheda-par-programma">
        <div className="font-head font-bold text-sm mb-1 flex items-center gap-2"><CalendarClock size={15} className="text-indigo-600" /> Programmazione misurazioni</div>
        <p className="text-xs text-muted-foreground mb-2.5">Ogni quanti giorni vanno misurati i parametri. L'ospite comparirà nel box <b>"Parametri da misurare oggi"</b> quando la misura è scaduta. Lascia vuoto o 0 per non programmare.</p>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">Ogni</span>
          <Input type="number" min="0" step="1" value={freq} onChange={(e) => setFreq(e.target.value)} className="w-20 h-9" data-testid="scheda-par-freq-input" placeholder="es. 7" />
          <span className="text-sm text-muted-foreground">giorni</span>
          <Button size="sm" onClick={saveFreq} disabled={freqBusy} data-testid="scheda-par-freq-save">Salva</Button>
          {savedFreq > 0 && (
            <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2.5 py-1" data-testid="scheda-par-freq-badge">Programmato: ogni {savedFreq} giorni</span>
          )}
        </div>
      </div>
      <div className="gr-card p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="font-head font-bold flex items-center gap-2"><HeartPulse size={16} className="text-rose-600" /> Parametri recenti</span>
          <Button size="sm" onClick={() => { setForm({ operatore: opName || "" }); setDlg(true); }} data-testid="scheda-par-rileva"><Plus size={14} className="mr-1" /> Rileva</Button>
        </div>
        {!latest ? <p className="text-sm text-muted-foreground">Nessuna misurazione registrata</p> : (
          <div className="flex flex-wrap gap-2">
            {PARAMS.map((p) => { const f = flag(p.key, latest[p.key]); return <span key={p.key} className={`text-xs rounded-lg px-2 py-1 border ${flagClass(f) || "bg-secondary"}`} data-testid={`scheda-par-${p.key}`}>{p.short}: <b>{latest[p.key] ?? "—"}</b> {latest[p.key] != null ? p.unit : ""}</span>; })}
            <span className="text-xs text-muted-foreground w-full mt-1">Ultima misura: {fmtDateTime(latest.data)} · {latest.operatore || "—"}</span>
          </div>
        )}
      </div>

      <div className="gr-card p-4">
        <div className="font-head font-bold text-sm mb-2 flex items-center gap-2"><Activity size={15} /> Andamento (temperatura / SpO₂)</div>
        <ParametriChart data={recs} />
      </div>

      <div className="gr-card p-4 overflow-x-auto">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="font-head font-bold text-sm flex items-center gap-2"><History size={15} /> Storico misurazioni</div>
          <div className="flex items-center gap-1" data-testid="scheda-par-mese">
            <Button variant="outline" size="sm" onClick={() => shiftMeseP(-1)} data-testid="scheda-par-mese-prev"><ChevronLeft size={16} /></Button>
            <button onClick={() => setMese(curMeseP())} className="min-w-[130px] text-center text-sm font-semibold px-2 py-1 rounded-md hover:bg-secondary transition-colors" title="Mese corrente" data-testid="scheda-par-mese-label">{meseLblP(mese)}</button>
            <Button variant="outline" size="sm" disabled={mese >= curMeseP()} onClick={() => shiftMeseP(1)} data-testid="scheda-par-mese-next"><ChevronRight size={16} /></Button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
            <th className="text-left p-2 whitespace-nowrap">Data/ora</th>
            {PARAMS.map((p) => <th key={p.key} className="p-2 text-center whitespace-nowrap" title={`${p.label} (${p.unit})`}>{p.label}</th>)}
            <th className="p-2 text-left">Operatore</th><th className="p-2"></th>
          </tr></thead>
          <tbody>
            {meseRecs.length === 0 && <tr><td colSpan={99} className="text-center py-6 text-muted-foreground">Nessuna misurazione in {meseLblP(mese)}</td></tr>}
            {meseRecs.map((rec) => (
              <tr key={rec.id} className="border-t" data-testid={`scheda-par-row-${rec.id}`}>
                <td className="p-2 whitespace-nowrap font-semibold">{fmtDateTime(rec.data)}</td>
                {PARAMS.map((p) => { const f = flag(p.key, rec[p.key]); return <td key={p.key} className="p-1 text-center"><span className={`inline-block min-w-[30px] rounded px-1 tabnum ${flagClass(f)}`}>{rec[p.key] ?? "—"}</span></td>; })}
                <td className="p-2 text-xs">{rec.operatore || "—"}</td>
                <td className="p-2 text-center"><Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => delRec(rec)}><Trash2 size={14} /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rileva parametri — {ospite.cognome} {ospite.nome}</DialogTitle>
            <DialogDescription>Inserisci i valori misurati. Lascia vuoti quelli non rilevati.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {PARAMS.map((p) => (
              <div key={p.key}>
                <Label className="text-xs font-semibold mb-1 block">{p.label} <span className="text-muted-foreground font-normal">{p.unit}</span></Label>
                <Input type="number" step="any" value={form[p.key] ?? ""} onChange={(e) => setForm((s) => ({ ...s, [p.key]: e.target.value }))} data-testid={`scheda-par-input-${p.key}`} />
              </div>
            ))}
          </div>
          <div className="mt-1">
            <Label className="text-xs font-semibold mb-1 block">Operatore *</Label>
            {opName ? <OperatoreLocked name={opName} testid="scheda-par-op" /> : (
            <Select value={form.operatore || ""} onValueChange={(v) => setForm((s) => ({ ...s, operatore: v }))}>
              <SelectTrigger data-testid="scheda-par-op"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{operatori.map((o) => <SelectItem key={o.id} value={`${o.nome} ${o.cognome}`}>{o.nome} {o.cognome}</SelectItem>)}</SelectContent>
            </Select>)}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Annulla</Button>
            <Button onClick={save} data-testid="scheda-par-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
